import React, { useState } from 'react';
import {
    Button,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Alert,
    CircularProgress
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useNavigate } from 'react-router-dom';
import { apiHandler } from '../../../utils/api/responseHandler';
import config from '../../../config';
import { useAuth } from '../../../hooks/auth/useAuth';
import logger from '../../../utils/logging/logger';
import { useCart } from '../../../utils/cart/CartContext';
import { triggerMismatchCheck } from '../../../utils/mismatchAlert';
import { trackClarityEvent, setClarityTag } from '../../../utils/analytics/clarityEvents';

const componentName = 'AddToCartButton';

interface AddToCartButtonProps {
    quoteId: number;
    boatId?: number;
    disabled?: boolean;
    variant?: 'contained' | 'outlined' | 'text';
    color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    size?: 'small' | 'medium' | 'large';
    fullWidth?: boolean;
}

const AddToCartButton: React.FC<AddToCartButtonProps> = ({
    quoteId,
    boatId,
    disabled = false,
    variant = 'contained',
    color = 'primary',
    size = 'medium',
    fullWidth = false
}) => {
    const [notes, setNotes] = useState('');
    const [isAdding, setIsAdding] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { refreshCart, openCart } = useCart();
    const { user, isAuthenticated, isAnonymous, isInitializing } = useAuth();
    const navigate = useNavigate();

    const handleAddToCart = async () => {
        // Close the dialog
        setShowDialog(false);

        // PHASE 1: Anonymous users can now add to cart
        // Email will be collected at checkout in CartDrawer

        // Safety check: Ensure user has a valid userId
        if (!user || !user.userId || user.userId === 0) {
            logger.log('LogError', componentName, 'No valid user ID available', { user });
            setError('Unable to add to cart. Please refresh the page and try again.');
            return;
        }

        setIsAdding(true);
        setError(null);

        try {
            logger.log('LogVerbose', componentName, 'Adding to cart', {
                userId: user.userId,
                quoteId: quoteId,
                isAnonymous: user.isAnonymous
            });

            const response = await apiHandler.fetch(
                `${config.API_URL}/api/order-quote`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userId: user.userId,
                        quoteId: quoteId,
                        notes: notes
                    })
                }
            );

            console.log('AddToCartButton - API Response:', response);

            if (response.error) throw response.error;

            logger.log('LogVerbose', componentName, 'Item added to cart successfully', response.data);

            // Trigger mismatch check
            triggerMismatchCheck();

            // Analytics
            trackClarityEvent('add_to_cart');
            setClarityTag('cart_action', 'add');

            setShowSuccess(true);
            setNotes(''); // Clear notes after successful addition

            // Refresh the cart with explicit userId to avoid ref timing issues.
            // Await ensures cart data (count + version) is updated before opening the drawer.
            await refreshCart(user.userId);

            // Auto-open the cart and navigate to sail selection after a short delay.
            // Navigation ensures user can't modify the quote (OrderStatusId is now 2,
            // so navigating back will create a new quote).
            setTimeout(() => {
                openCart();
                if (boatId) {
                    navigate(`/quoter/sail-selection/${boatId}`);
                }
            }, 1000);

        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to add item to cart';
            logger.log('LogError', componentName, 'Error adding to cart', {
                error: errorMessage,
                userId: user.userId,
                quoteId: quoteId,
                fullError: err
            });
            setError(errorMessage);
        } finally {
            setIsAdding(false);
        }
    };

    const openDialog = () => {
        // PHASE 1: Ensure user account exists before adding to cart
        // Log current user state for debugging
        console.log('AddToCartButton - User state:', {
            userId: user?.userId,
            isAnonymous: user?.isAnonymous,
            isAuthenticated: isAuthenticated,
            isInitializing: isInitializing,
            fullUser: user
        });

        // If user is still initializing or has no userId, don't open dialog yet
        if (isInitializing) {
            logger.log('LogWarning', componentName, 'User still initializing, please wait');
            setError('Please wait a moment while we set up your session...');
            return;
        }

        if (!user || !user.userId || user.userId === 0) {
            logger.log('LogError', componentName, 'No valid user ID when opening cart dialog', { user });
            setError('Unable to add to cart. Please refresh the page and try again.');
            return;
        }

        // Email will be collected at checkout in CartDrawer
        setShowDialog(true);
    };

    const closeDialog = () => {
        setShowDialog(false);
    };

    const closeSuccess = () => {
        setShowSuccess(false);
    };

    const closeError = () => {
        setError(null);
    };

    return (
        <>
            <Button
                variant={variant}
                color={color}
                startIcon={<ShoppingCartIcon />}
                onClick={openDialog}
                disabled={disabled || isAdding || !quoteId}
                size={size}
                fullWidth={fullWidth}
                sx={{ mt: 1 }}
            >
                {isAdding ? 'Adding...' : 'Add to Cart'}
            </Button>

            {/* Dialog for adding notes */}
            <Dialog open={showDialog} onClose={closeDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Add to Cart</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Would you like to add any special instructions or questions for this sail?
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="notes"
                        label="Special Instructions (Optional)"
                        type="text"
                        fullWidth
                        multiline
                        rows={4}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        variant="outlined"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddToCart}
                        color="primary"
                        variant="contained"
                        disabled={isAdding}
                        startIcon={isAdding ? <CircularProgress size={20} /> : <ShoppingCartIcon />}
                    >
                        {isAdding ? 'Adding...' : 'Add to Cart'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success message */}
            <Snackbar
                open={showSuccess}
                autoHideDuration={5000}
                onClose={closeSuccess}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={closeSuccess} severity="success" sx={{ width: '100%' }}>
                    Item added to cart successfully!
                </Alert>
            </Snackbar>

            {/* Error message */}
            <Snackbar
                open={!!error}
                autoHideDuration={5000}
                onClose={closeError}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={closeError} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </>
    );
};

export default AddToCartButton;